import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";

import { connect } from "react-redux";

import { Autocomplete, ListItemUser, DotsLoader } from "@similarweb/ui-components";

import { showSuccessToast } from "app/actions/toast_actions";
import { Header } from "pro-features/components/Dashboard/SharedDashboardModal";
import { ProModal } from "pro-features/components/Modals/src/ProModal";
import ImpersonateAuthCtrl from "app/components/React/ImpersonateAuth/ImpersonateAuthCtrl";
import ImpersonateAuthModal from "app/components/React/ImpersonateAuth/ImpersonateAuthModal";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import fetchService from "app/common/services/salesFetchService";

export interface IUser {
    AccountId: string;
    AccountName: string;
    Id: string;
    Role: string;
    UserName: string;
}
interface IShareDialog {
    dialogIsOpen: boolean;
    setDialogIsOpen: (isOpen: boolean) => void;
    onClickUser: (user: IUser) => Promise<boolean>;
    generateToastMessage?: (user: IUser, isSucceeded: boolean) => string;
    titleText: string;
    showCopyToSuccess: (message: string) => void;
    usersApiAction: string;
}

const ShareExternalDialog: FC<IShareDialog> = ({
    dialogIsOpen,
    setDialogIsOpen,
    onClickUser,
    generateToastMessage,
    titleText,
    showCopyToSuccess,
    usersApiAction,
}) => {
    const [showImpersonateAuth, setShowImpersonateAuth] = useState<boolean>(false);

    useEffect(() => {
        if (dialogIsOpen) {
            (async () => {
                setShowImpersonateAuth(true);
                try {
                    const shouldAuthenticate = await ImpersonateAuthCtrl.shouldAuthenticate();
                    if (!shouldAuthenticate) {
                        setShowImpersonateAuth(false);
                        setDialogIsOpen(true);
                    }
                } catch (error) {}
            })();
        } else {
            setShowImpersonateAuth(false);
        }
    }, [dialogIsOpen]);

    const onClose = () => {
        setDialogIsOpen(false);
    };

    const handleOnClickUser = useCallback(
        async (user) => {
            const isSucceeded = await onClickUser(user);
            generateToastMessage && showCopyToSuccess(generateToastMessage(user, isSucceeded));
        },
        [onClickUser, showCopyToSuccess, generateToastMessage],
    );

    const getListItems = async (query) => {
        const url = `/useridentity/getidentities?action=${usersApiAction}&token=${query.trim()}`;
        const { Result: users } = await fetchService.get(url);
        return users.slice(0, 10).map((user: IUser) => {
            const { UserName, Id, AccountName } = user;
            return (
                <ListItemUser
                    key={Id.toString()}
                    email={AccountName}
                    name={UserName}
                    onClick={() => handleOnClickUser(user)}
                />
            );
        });
    };
    const onImpersonateVerification = () => {
        setShowImpersonateAuth(false);
    };
    const onImpersonateClose = () => {
        setShowImpersonateAuth(false);
        setDialogIsOpen(false);
    };

    const proModalProps = {
        customStyles: {
            content: {
                padding: "24px",
                width: "580px",
            },
        },
    };

    return (
        <>
            {showImpersonateAuth ? (
                <ImpersonateAuthModal
                    onVerify={onImpersonateVerification}
                    onClose={onImpersonateClose}
                />
            ) : null}
            <ProModal
                isOpen={dialogIsOpen}
                onCloseClick={onClose}
                {...proModalProps}
            >
                <Header>
                    <I18n>{titleText}</I18n>
                </Header>
                <Autocomplete
                    getListItems={getListItems}
                    loadingComponent={<DotsLoader />}
                    debounce={400}
                />
            </ProModal>
        </>
    );
};

const mapDispatchToProps = (dispatch) => {
    return {
        showCopyToSuccess: (message) => {
            dispatch(showSuccessToast(message));
        },
    };
};

export default connect(null, mapDispatchToProps)(ShareExternalDialog);
